import { useCallback, useMemo } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import type { MapMouseEvent } from "react-map-gl/mapbox";
import type { PlaceFeature, PlaceGeoJSON } from "../../../lib/api";
import { PLACES_LAYER_ID, PLACES_SOURCE_ID, placesLayout, placePaint } from "../../../lib/mapConstants";
import { useMapEvent } from "../hooks/useMapEvent";
import { useCursorOnHover } from "../hooks/useCursorOnHover";

interface PlacesLayerProps {
  /** GeoJSON FeatureCollection of places to display */
  places: PlaceGeoJSON;
  /** Place IDs to exclude from rendering (e.g., related places shown in another layer) */
  excludeIds?: string[];
  /** Called when a place is clicked */
  onClick?: (feature: PlaceFeature) => void;
}

/**
 * Renders database places with standard white icons (poi-{type}).
 * Handles click events and cursor feedback internally.
 */
export function PlacesLayer({ places, excludeIds, onClick }: PlacesLayerProps) {
  const filteredPlaces = useMemo((): PlaceGeoJSON => {
    if (!excludeIds || excludeIds.length === 0) return places;
    const excludeSet = new Set(excludeIds);
    return {
      ...places,
      features: places.features.filter((f) => !excludeSet.has(f.id as string)),
    };
  }, [places, excludeIds]);

  // Handle click - find the full feature from our data
  const handleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!onClick) return;
      const clickedFeature = e.features?.[0];
      if (!clickedFeature?.properties?.id) return;

      // Find the full feature from our places data (event features lose some properties)
      const placeId = clickedFeature.properties.id as string;
      const feature = places.features.find((f) => f.id === placeId);
      if (feature) {
        onClick(feature);
      }
    },
    [places.features, onClick],
  );

  useMapEvent("click", PLACES_LAYER_ID, handleClick);
  useCursorOnHover(PLACES_LAYER_ID);

  return (
    <Source id={PLACES_SOURCE_ID} type="geojson" data={filteredPlaces} attribution={places.attribution}>
      <Layer id={PLACES_LAYER_ID} type="symbol" layout={placesLayout} paint={placePaint} />
    </Source>
  );
}
